import { useEffect, useState } from 'react';
import { SITE_NAME, SITE_URL } from '../seoData';
import { getHeadquartersLocation } from '../utils/agentService';

// Site-wide Organization JSON-LD — rendered once (not per-route) since this
// info doesn't change page to page. Plain inline <script>, not react-helmet-
// async's <Helmet>: Helmet's tags don't reliably commit to this app's live
// DOM (see FaqSchema.jsx/LocalBusinessSchema.jsx for the same reasoning), a
// direct <script> sidesteps that entirely.
//
// No street address on the base schema: Boomers Insurance is licensed across
// 46 states with no single storefront, so a plain `address` field here would
// misrepresent the org as having one location. If the admin has marked a
// location as the headquarters (LocalBusinessSchema.jsx), though, that's
// exactly the "SEO should point at this one first" signal the client asked
// for — so we embed it here as the Organization's own address once loaded.
export default function OrganizationSchema() {
  const [hq, setHq] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getHeadquartersLocation()
      .then((res) => {
        if (cancelled) return;
        const location = res?.data?.data?.[0];
        if (location) setHq(location);
      })
      .catch(() => {
        /* silent — Organization schema still renders correctly without an address */
      });
    return () => { cancelled = true; };
  }, []);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.ico`,
    telephone: '+1-800-815-1943',
    areaServed: 'US',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-800-815-1943',
      contactType: 'customer service',
      areaServed: 'US',
      availableLanguage: 'English',
    },
    ...(hq && {
      address: hq.full_address,
      location: { '@id': `${SITE_URL}/locations#headquarters` },
    }),
  };

  return <script type="application/ld+json">{JSON.stringify(schema)}</script>;
}
